// bottom-pane.shell.ts
import { ChangeDetectionStrategy, Component, Input, inject, HostBinding, effect, Directive, OnInit, OnDestroy, EventEmitter, Output, ElementRef, ViewChild } from '@angular/core';
import { PortalModule, ComponentPortal, TemplatePortal } from '@angular/cdk/portal';
import { CdkTrapFocus } from '@angular/cdk/a11y';
import { CommonModule, NgClass, NgStyle } from '@angular/common';
import { BOTTOM_PANE_STATE } from './bottom-pane.tokens';
import { BottomPaneConfig } from './bottom-pane.types';
import { BottomPaneRef } from './bottom-pane.ref';
import { SwipeDirective } from '../../pipes/swipe.directive';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';

@Component({
  standalone: true,
  selector: 'bp-shell',
  imports: [PortalModule, CdkTrapFocus, NgClass, NgStyle, CommonModule, SwipeDirective, MatIconModule],
  template: `
    <section
      swipeBehavior
      [scrollContainer]="contentElement"
      (swipeDown)="close()"
      cdkTrapFocus
      class="bp-shell"
      role="dialog"
      [attr.aria-label]="config?.ariaLabel || 'Bottom pane'"
      [ngClass]="config?.panelClass"
      [ngStyle]="{ height: '100%', width: '100%' }"
    >
      @if (showHeader){
      <header class="bp-shell__header">
         <ng-content select="[bpTitle]"></ng-content> 
         
        @if (!config?.disableClose){
          <div class="bp-shell__header_bar">
          <div class="drag-bar"></div>
          <button matIconButton type="button" class="close" (click)="closeClick()"><mat-icon>close</mat-icon></button>
           </div>
        }         
        @if (paneRef.title()) {         
            <h4 class="bottom-pane-title">{{ paneRef.title()}}</h4>
             @if (paneRef.subTitle()) {
              <div class="bottom-pane-header flex-container container-space-between column">
                   <div class="list-sub-header">{{ paneRef.subTitle()}}</div>
             </div>
              }
          }
      </header>
      }
      <div #contentDiv class="bp-shell__content">
        <ng-template [cdkPortalOutlet]="contentPortal" (attached)="onPortalAttached($event)"></ng-template>
      </div>
    </section>
  `,
  styles: [`
    :host { display: block;  }/*width: 100%;*/
    .bp-shell {
      box-sizing: border-box;
      background: var(--dialog-background, #fff);
      color: var(--mat-sys-on-surface-variant, #222);
      border-top-left-radius: 16px;
      border-top-right-radius: 16px;
      box-shadow: var(--mat-sys-level1);/* 0 -6px 16px rgba(0,0,0,.2)*/
      max-height: 98vh;
      /*overflow: hidden;*/
      animation: bp-slide-up .25s ease-out;
      
    }
    .bp-shell.closing {
      animation: bp-slide-down .1s ease-in forwards;
    }
    .bp-shell__header_bar {
      display: flex; align-items: center;
      justify-content: flex-end;
      padding-top: .25rem;
      padding-bottom: .25rem;
      position: relative;
    }
    .bp-shell__content { 
      height: 100%;
    }
    .bp-shell__close {
      border: 0;
      background: transparent; 
      font-size: 18px; 
      cursor: pointer; 
      line-height: 1;
    }

    
    .bp-shell.swiping-closed {
        animation: none !important; /* Disable the bp-slide-up animation */
      }

    @keyframes bp-slide-up {
      from { transform: translateY(48px); opacity: .0; }/*16*/
      to   { transform: translateY(0); opacity: 1; }
    }

    @keyframes bp-slide-down {
      from { transform: translateY(0); opacity: 1; }
      to   { transform: translateY(48px); opacity: 0; }/*16*/
    }
    .drag-bar {
    width: 60px;
    height: 4px;
    background: var(--mat-sys-outline-variant);
    border-radius: 4px;
    margin: 8px auto;
    @media (min-width: 599px) {
        background: transparent;
    }
  }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BottomPaneShellComponent implements OnInit, OnDestroy {
  @Input() contentPortal!: ComponentPortal<unknown>;
  @Input() config?: BottomPaneConfig;
  @Input() showHeader = true;

  @ViewChild('contentDiv', { read: ElementRef }) contentDivRef?: ElementRef;
  contentElement: HTMLElement | null = null;

  @Output() contentAttached = new EventEmitter<any>();

  private router = inject(Router);
  private lastPath = '';
  private sub?: Subscription;


  private readonly paneRef = inject(BottomPaneRef);
  private readonly state = inject(BOTTOM_PANE_STATE);

  heightPx = 'auto';

  constructor() {
    effect(() => {
      const h = this.state.height();
      this.heightPx = typeof h === 'number' ? `${h}vh` : 'auto';
    });
  }

  onPortalAttached(ref: any) {
    if (ref?.instance) {
      this.contentAttached.emit(ref.instance);
    }
  }

  ngOnInit(): void {

    this.lastPath = this.cleanPath(this.router.url);

    this.sub = this.router.events
      .pipe(filter(ev => ev instanceof NavigationEnd))
      .subscribe((ev: NavigationEnd) => {
        const newPath = this.cleanPath(ev.urlAfterRedirects);

        if (newPath !== this.lastPath) {
          this.lastPath = newPath;
          this.onMainRouteChanged(newPath);
        }
      });

  }
  ngAfterViewInit() {
    // scrollable content element
    setTimeout(() => {
      this.contentElement = this.contentDivRef?.nativeElement.querySelector('.bottom-pane-content') ||
        this.contentDivRef?.nativeElement;
    }, 100);
  }

  ngOnDestroy() {
    this.sub?.unsubscribe();
  }

  private cleanPath(url: string): string {
    return url.split('?')[0].split('#')[0];
  }

  onMainRouteChanged(path: string) {
    this.close();
  }


  close() {
    this.paneRef.close();
  }
  closeClick() {
    this.paneRef.closeClick();
  }

  @HostBinding('class.bp-minimized')
  get minimized() { return this.state.minimized(); }
}